import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {StaticImageData} from 'next/image';
import DisplayImage from '../display-image';
import parse from 'html-react-parser';
import {fadeInUp} from '@/animation/scroll-animation';
import dynamic from 'next/dynamic';
import {colors} from '@/styles/tokens';

const AnimatedComponent = dynamic(() => import('../AnimatedComponent').then((mod) => mod.AnimatedComponent));

interface TopPlatformsProps {
    heading: string;
    subheading: string;
    data: {
        src: string | StaticImageData;
        alt: string;
        label?: string;
    }[];
}

const logoBoxSx = {
    width: {xs: 80, sm: 90, md: 100, lg: 110},
    height: {xs: 90, sm: 100, md: 110, lg: 120},
    borderRadius: 3,
    border: '1px solid #EBEBEB',
    bgcolor: colors.background.default,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    p: 2,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    textAlign: 'center'
} as const;

const TopPlatforms = ({heading, subheading, data}: TopPlatformsProps) => {
    return (
        <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        {heading ? parse(heading) : ''}
                    </Typography>

                    <Typography variant="subtitle1" component="p" className="font-regular-text remove-underline" sx={{mb: {xs: 2, md: 6}}}>
                        {subheading ? parse(subheading) : ''}
                    </Typography>
                </Box>

                <AnimatedComponent variants={fadeInUp}>
                    <Box className="banner-bottom-container-wrapper">
                        {[0, 1]?.map((_, rowIdx) => (
                            <Box key={rowIdx} display="flex" gap={4} className="banner-bottom-container">
                                {data?.map((logo, idx) => (
                                    <Box key={`${rowIdx}-${idx}`} sx={logoBoxSx}>
                                        <Box position="relative" width={50} height={50} mb={1}>
                                            <DisplayImage quality={100} src={logo.src} alt={logo.alt} fill style={{objectFit: 'contain'}} />
                                        </Box>

                                        {logo.label && (
                                            <Typography
                                                className="font-regular-text"
                                                component="p"
                                                sx={{
                                                    fontSize: '12px',
                                                    color: colors.text.primary,
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {logo.label}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </AnimatedComponent>
            </MUIContainer>
        </Box>
    );
};

export default memo(TopPlatforms);
